/**
 * Statistics Service - Fetches statistics metadata for tables
 */

import type { DatabaseEngineId } from '@/core/engines';
import { executeQueryV2 } from './queryExecutor';
import { createExplorerExecutionId } from './explorerQueryUtils';
import { getQueries } from './queries';

export interface StatisticInfo {
  name: string;
  columns: string[];
  autoCreated: boolean;
  userCreated: boolean;
  lastUpdated: string | null;
}

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';
  return false;
}

function getRowValue(row: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

/**
 * Groups raw rows by statistic_name and collects columns in ordinal order
 */
export function mapStatisticInfoRows(rows: Record<string, unknown>[]): StatisticInfo[] {
  const grouped = new Map<string, {
    columns: { name: string; ordinal: number }[];
    autoCreated: boolean;
    userCreated: boolean;
    lastUpdated: string | null;
  }>();

  for (const row of rows) {
    const name = toStr(getRowValue(row, ['statistic_name']));
    if (!name) continue;

    const columnName = toStr(getRowValue(row, ['column_name']));
    const ordinal = Number(getRowValue(row, ['column_ordinal']) ?? 0);

    if (!grouped.has(name)) {
      const lastUpdatedRaw = getRowValue(row, ['last_updated']);
      const lastUpdated = lastUpdatedRaw != null ? toStr(lastUpdatedRaw) : null;

      grouped.set(name, {
        columns: [],
        autoCreated: toBool(getRowValue(row, ['auto_created'])),
        userCreated: toBool(getRowValue(row, ['user_created'])),
        lastUpdated: lastUpdated || null,
      });
    }

    const entry = grouped.get(name)!;
    if (columnName && !entry.columns.some((c) => c.name === columnName)) {
      entry.columns.push({ name: columnName, ordinal });
    }
  }

  const result: StatisticInfo[] = [];
  for (const [name, data] of grouped) {
    data.columns.sort((a, b) => a.ordinal - b.ordinal);
    result.push({
      name,
      columns: data.columns.map((c) => c.name),
      autoCreated: data.autoCreated,
      userCreated: data.userCreated,
      lastUpdated: data.lastUpdated,
    });
  }

  return result;
}

/**
 * Extract the date portion (YYYY-MM-DD) from a datetime string.
 * Returns null if the input is empty or unparseable.
 */
function formatDateOnly(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const match = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

/**
 * Format a statistic's display name in SSMS style
 * e.g. "_WA_Sys_ProductName (ProductName) [auto_created, updated: 2025-02-20]"
 */
export function formatStatisticDisplayName(stat: StatisticInfo): string {
  const columnsPart = stat.columns.length > 0 ? ` (${stat.columns.join(', ')})` : '';
  const flags: string[] = [];
  if (stat.autoCreated) flags.push('auto_created');
  if (stat.userCreated) flags.push('user_created');
  const dateOnly = formatDateOnly(stat.lastUpdated);
  if (dateOnly) flags.push(`updated: ${dateOnly}`);
  const flagsPart = flags.length > 0 ? ` [${flags.join(', ')}]` : '';

  return `${stat.name}${columnsPart}${flagsPart}`;
}

/** Engines that do not support statistics */
const UNSUPPORTED_ENGINES = new Set(['mysql', 'sqlite']);

export async function getTableStatistics(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  engine?: DatabaseEngineId | string
): Promise<{ statistics: StatisticInfo[]; unsupported?: boolean; error?: string }> {
  if (engine && UNSUPPORTED_ENGINES.has(engine)) {
    return { statistics: [], unsupported: true };
  }

  const queries = getQueries(engine);

  try {
    const result = await executeQueryV2({
      connectionId,
      sql: queries.tableStatistics(schema, tableName),
      database,
      executionId: createExplorerExecutionId(connectionId),
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[StatisticsService] Error getting statistics: ${errorMsg}`);
      return { statistics: [], error: errorMsg };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    const statistics = mapStatisticInfoRows(rows);

    return { statistics };
  } catch (error) {
    console.error('[StatisticsService] Error in getTableStatistics:', error);
    return {
      statistics: [],
      error: error instanceof Error ? error.message : 'Unknown error getting statistics',
    };
  }
}
